import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { useState, useContext, createContext } from 'react';
import { signOut, decodeJWT, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';

import { ENVIRONMENT } from '../environment';
import { useSessionExpiry } from '../hooks/custom/use-session-expiry';
import { SessionExpiredDialog } from '../components/modals/session-expired';

const { userPoolId, clientId, domain, redirectSignIn, redirectSignOut, resourceManagerGroupId } =
  ENVIRONMENT.cognito;

interface CustomAuthState {
  redirect?: string;
}

type IAuthContext = {
  isLoggedIn: () => Promise<boolean>;
  login: (redirect?: string) => void;
  logout: () => void;
  customAuthState: CustomAuthState | undefined;
  getIdToken: () => Promise<string | undefined>;
  isSessionExpired: boolean;
  userId?: string;
  isUserResourceManager: boolean;
};

export type Session = {
  userName: string;
  userId: string;
};

const AuthContext = createContext<IAuthContext | null>(null);

const AuthProvider = ({ children }: any) => {
  const [customAuthState, setCustomAuthState] = useState<CustomAuthState>();
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const { setupSessionTimer } = useSessionExpiry(setIsSessionExpired);
  const [userId, setUserId] = useState<string | undefined>();
  const [isUserResourceManager, setIsUserResourceManager] = useState<boolean>(false);

  const getIdToken = async (): Promise<string | undefined> => {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      setupSessionTimer(session.tokens.idToken.toString());
      const decodedToken = decodeJWT(session.tokens.idToken.toString());

      const extractedUserId = decodedToken?.payload['custom:uuid'];
      const groups = decodedToken?.payload['cognito:groups'];

      if (typeof extractedUserId === 'string') {
        setUserId(extractedUserId);
      }
      if (Array.isArray(groups)) {
        const isUserResourceManager = groups.includes(resourceManagerGroupId);
        setIsUserResourceManager(isUserResourceManager);
      }
    }
    return session.tokens?.idToken?.toString();
  };

  const handleSessionExpired = () => {
    setIsSessionExpired(false);
    // TODO: it's usually good practice to invoke an FE logout mechanism at this point to clean up any FE session data
    // logout();
  };

  const isLoggedIn = async () => !!(await getIdToken());

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId: clientId,
        loginWith: {
          // Optional
          oauth: {
            domain,
            scopes: ['openid', 'email', 'phone', 'profile', 'aws.cognito.signin.user.admin'],
            redirectSignIn: [redirectSignIn],
            redirectSignOut: [redirectSignOut],
            responseType: 'code',
          },
        },
      },
    },
  });

  Hub.listen('auth', (data) => {
    const { payload } = data;
    if (payload.event === 'customOAuthState') {
      setCustomAuthState(JSON.parse(payload.data));
    }
  });

  const login = async (redirect?: string) => {
    if (!(await isLoggedIn())) {
      signInWithRedirect({
        customState: JSON.stringify({
          redirect,
        }),
        provider: {
          custom: 'AzureADLogin',
        },
      });
    }
  };

  return (
    <>
      <AuthContext.Provider
        value={{
          isLoggedIn,
          login,
          logout: signOut,
          customAuthState,
          getIdToken,
          isSessionExpired,
          userId,
          isUserResourceManager,
        }}
      >
        {children}
      </AuthContext.Provider>

      <SessionExpiredDialog
        isOpen={isSessionExpired}
        onClose={handleSessionExpired}
      />
    </>
  );
};

const useAuthContext = (): IAuthContext => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuthContext must be used within a AuthProvider');
  return context;
};

export { AuthProvider, useAuthContext };
